import { ref, onMounted } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { useToast } from '@/composables/common/useToast';

/**
 * Composable para la vista de editar abonado (back.abonados.editar).
 *
 * Mantiene el objeto COMPLETO del abonado (como el legacy) para que al enviar
 * el formulario se manden TODOS los datos incluyendo objetos anidados
 * (precio, descuento, plaza, user, transacciones, etc.).
 *
 * @param {{ lista_id: number|string, abonado_id: number|string }} props - route params
 * @param {{ onSaved: () => void }} options - callbacks
 */
export function useEditarAbonado(props, options = {}) {
    const toast = useToast();

    const loading = ref(true);
    const submitting = ref(false);

    /** Objeto completo del abonado (tal cual lo devuelve el API) */
    const abonado = ref(null);

    /**
     * Carga los datos completos del abonado.
     */
    async function fetchAbonado() {
        loading.value = true;

        try {
            const params = {
                abonado_id: props.abonado_id,
                filters: {
                    lista_abonado_id: props.lista_id,
                },
            };

            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.show'),
                params,
            );

            abonado.value = res.data.data;

            // Legacy quirk: remove stray 'i' from n_abonado
            if (typeof abonado.value.n_abonado === 'string') {
                abonado.value.n_abonado = abonado.value.n_abonado.replace('i', '');
            }
        } catch (err) {
            console.error('Error fetching abonado:', err);
            toast.error('Error al cargar los datos del abonado');
        } finally {
            loading.value = false;
        }
    }

    /**
     * Envía el formulario con el objeto COMPLETO del abonado.
     */
    async function submit() {
        if (submitting.value || !abonado.value) return;
        submitting.value = true;

        try {
            const payload = {
                ...abonado.value,
                abonado_id: props.abonado_id,
                filters: {
                    lista_abonado_id: props.lista_id,
                },
            };
            // El API devuelve 'movil' pero el backend actualiza 'telefono_movil'
            payload.telefono_movil = payload.movil;
            delete payload.movil;

            await store.dispatch(
                config.getVuexAlias('back.abonados.editar'),
                payload,
            );

            toast.success('Abonado actualizado correctamente');

            if (options.onSaved) {
                options.onSaved();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al actualizar el abonado';
            toast.error(msg);
            console.error('Error updating abonado:', err);
        } finally {
            submitting.value = false;
        }
    }

    onMounted(() => {
        fetchAbonado();
    });

    return {
        abonado,
        loading,
        submitting,
        fetchAbonado,
        submit,
    };
}
